export const DEFAULT_THUMBNAIL_SIZE = { width: 1280, height: 720 }

/**
 * Hover-preview images load in CORS mode, but the same URLs (notably the
 * episode artwork) are cached from non-CORS loads without CORS headers, which
 * makes the CORS request fail. A dedicated query param gives them their own
 * cache key; it goes before any #fragment.
 */
export function thumbnailUrl(url) {
    const hashAt = url.indexOf("#")
    const base = hashAt === -1 ? url : url.slice(0, hashAt)
    const fragment = hashAt === -1 ? "" : url.slice(hashAt)

    return `${base}${base.includes("?") ? "&" : "?"}vp=thumb${fragment}`
}

/**
 * Chapters → [{id, start, end, text, image}] on the timeline mapTime() maps
 * to. Each chapter runs until the next one starts; the last runs until the
 * end. Chapters that collapse onto the same start (e.g. two that both fall
 * inside a pre-roll) keep only the later one.
 */
export function buildChapterCues(chapters, durationSeconds, mapTime = (seconds) => seconds) {
    if (!Array.isArray(chapters) || chapters.length === 0 || !(durationSeconds > 0)) {
        return []
    }

    const starts = chapters
        .map((chapter) => ({
            id: String(chapter.id),
            published: chapter.from_time ?? 0,
            start: Math.min(Math.max(mapTime(chapter.from_time ?? 0), 0), durationSeconds),
            text: chapter.title ?? "",
            image: chapter.image ?? null,
        }))
        .sort((a, b) => a.start - b.start || a.published - b.published)

    return starts
        .map(({ published, ...cue }, index) => ({ ...cue, end: index + 1 < starts.length ? starts[index + 1].start : durationSeconds }))
        .filter((cue) => cue.end > cue.start)
}

/**
 * Chapter cues → media-chrome "thumbnails" metadata cues. media-chrome
 * requires a #xywh fragment on every cue and matches the first/last cue for
 * times outside all cues, so every chapter gets a cue: chapters without an
 * image fall back to the episode artwork. Returns [] when no chapter has an
 * image, so the track is only added when there is something to preview.
 */
export function buildThumbnailCues(chapterCues, fallbackImage, sizes = new Map()) {
    if (!chapterCues.some((cue) => typeof cue.image === "string" && cue.image !== "")) {
        return []
    }

    return chapterCues
        .map((cue) => ({ ...cue, url: cue.image || fallbackImage }))
        .filter((cue) => typeof cue.url === "string" && cue.url !== "")
        .map((cue) => {
            const { width, height } = sizes.get(cue.url) ?? DEFAULT_THUMBNAIL_SIZE

            return { start: cue.start, end: cue.end, text: `${thumbnailUrl(cue.url)}#xywh=0,0,${width},${height}` }
        })
}
